/* eslint-disable react/prop-types */
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin, Dropdown, Menu, Tooltip } from 'antd';
import {
  DownloadOutlined,
  SearchOutlined,
  FilePdfOutlined,
  FileZipOutlined,
} from '@ant-design/icons';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { PageTitle } from 'components/SalaryComponents/PageTitle';
import { EMPLOYEE_HEADER } from 'const/translations/SalaryEmployeePage';
import {
  SALARY_HEADER_LABELS,
  getSilaeRefreshEmployeesTranslation,
} from 'const/translations';
import { MonthPicker } from 'components/ui/MonthPicker';
import { RangePicker } from 'components/ui/DatePicker';
import { CalendarMiniIcon } from 'components/ui/CalendarMiniIcon';
import {
  downloadEmployeesTableXlsx,
  downloadCompanyEmployeesPayslips,
  refreshSilaeEmployees,
} from 'modules/SalaryModule/Employees/actions';
import { compareByLabel } from 'helpers/common';
import { ROUTES, ISO_DATE_FORMAT } from 'const';
import {
  selectCompanyBranchesOptions,
  selectIsWaitingModalShow,
  selectIsSingleWaitingModalShow,
} from 'modules/SalaryModule/Employees/selectors';
import { getEmployeeTableCsvCommonFilter } from 'modules/SalaryModule/Employees/utils';
import iconFilterList from 'assets/icons/filter-list.svg';
import { MobileFilters } from 'containers/Salary/EmployeesPage/MobileFilters';
import { useSimpleMedia } from 'hooks/useMedia';
import { Button, CustomSelect, Input } from 'components/ui';
import refreshIcon from 'assets/icons/refresh-icon.svg';
import Styles from './FiltersToolbar.module.css';

const SCREEN_WIDTH_BREAKPOINT_VALUE = 796;
const SCREEN_WIDTH_BREAKPOINT_MEDIA = `(max-width: ${SCREEN_WIDTH_BREAKPOINT_VALUE}px)`;
const MONTH_PICKER_FORMAT = 'MMM YYYY';

const SHORT_MONTH_FR = [
  'Janv.',
  'Févr.',
  'Mars',
  'Avr.',
  'Mai',
  'Juin',
  'Juil.',
  'Août',
  'Sept.',
  'Oct.',
  'Nov.',
  'Déc.',
];

const {
  BUTTONS_LABELS,
  FILTER_PLACEHOLDERS,
  WAITING_MODAL,
  SINGLE_WAITING_MODAL,
  CHOSE_MODAL,
} = EMPLOYEE_HEADER;

const {
  DOWNLOAD,
  DOWNLOAD_PAYSLIP,
  NEW_EMPLOYEE,
  DOWNLOAD_PAYMENT_STATUS,
  DOWNLOAD_PAY_JOURNAL,
  DOWNLOAD_PAY_SUMMARY,
  DOWNLOAD_REST_BALANCE,
  DOWNLOAD_CHARGES_TABLE,
  DOWNLOAD_SALARY_COSTS,
  DOWNLOAD_CONTRIBUTION_DETAILS,
  SILAE_SYNCING,
} = BUTTONS_LABELS;

const propTypes = {
  companyId: PropTypes.string,
  natureEmploymentsOptions: PropTypes.array,
  classificationsOptions: PropTypes.array,
  contractsOptions: PropTypes.array,
  filters: PropTypes.object,
  searchTerm: PropTypes.string,
  dateRange: PropTypes.object,
  hasFilters: PropTypes.bool,
  onSelectChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onRangeChange: PropTypes.func.isRequired,
  resetSearch: PropTypes.func.isRequired,
  managedSilae: PropTypes.bool,
  lastSyncTime: PropTypes.string,
  lastSyncingStatus: PropTypes.string,
};

const menuOptions = [
  {
    value: 'payment-status',
    label: DOWNLOAD_PAYMENT_STATUS,
  },
  {
    value: 'payroll-journal',
    label: DOWNLOAD_PAY_JOURNAL,
  },
  {
    value: 'payroll-summary',
    label: DOWNLOAD_PAY_SUMMARY,
  },
  {
    value: 'rest-balance',
    label: DOWNLOAD_REST_BALANCE,
  },
  {
    value: 'table-charges',
    label: DOWNLOAD_CHARGES_TABLE,
  },
  {
    value: 'salary-costs',
    label: DOWNLOAD_SALARY_COSTS,
  },
  {
    value: 'contributions-details',
    label: DOWNLOAD_CONTRIBUTION_DETAILS,
  },
];

export const FiltersToolbar = memo(
  ({
    natureEmploymentsOptions,
    classificationsOptions,
    contractsOptions,
    filters,
    searchTerm,
    dateRange,
    hasFilters,
    onSelectChange,
    onSearchChange,
    onSubmit,
    onRangeChange,
    resetSearch,
    companyId,
    managedSilae,
    lastSyncTime,
    lastSyncingStatus,
  }) => {
    const [isModalVisible, setModalVisibility] = useState(false);
    const [isFormatModalVisible, setFormatModalVisibility] = useState(false);
    const [isInputVisible, setInputVisibility] = useState(false);
    const isMobileVersion = useSimpleMedia(SCREEN_WIDTH_BREAKPOINT_MEDIA);
    const searchInputRef = useRef(null);
    const branchesOptions = useSelector(selectCompanyBranchesOptions);
    const dispatch = useDispatch();

    const sortedNatureEmploymentsOptions =
      natureEmploymentsOptions.sort(compareByLabel);
    const sortedClassificationsOptions =
      classificationsOptions.sort(compareByLabel);
    const sortedContractsOptions = contractsOptions.sort(compareByLabel);

    useEffect(() => {
      if (searchInputRef && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [searchInputRef, isInputVisible]);

    useEffect(() => {
      if (branchesOptions.length === 1) {
        onSelectChange('establishments', branchesOptions[0].value || '');
      }
    }, [branchesOptions, onSelectChange]);

    const openModal = useCallback(() => {
      setModalVisibility(true);
    }, []);

    const closeModal = useCallback(() => {
      setModalVisibility(false);
    }, []);

    const toggleInput = () => {
      if (isInputVisible) {
        resetSearch();
      }
      setInputVisibility(!isInputVisible);
    };

    const handleSubmit = useCallback(
      (values) => {
        onSubmit(values);
        setModalVisibility(false);
      },
      [onSubmit]
    );

    const downloadTableXlsxHandler = useCallback(() => {
      dispatch(
        downloadEmployeesTableXlsx({
          companyId,
          filter: getEmployeeTableCsvCommonFilter(
            filters,
            searchTerm,
            dateRange
          ),
        })
      );
    }, [filters, searchTerm, dateRange, dispatch, companyId]);

    const downloadCompanyEmployeesPayslipsHandler = useCallback(
      ({ key }) => {
        if (key) {
          const { startDate, endDate } = dateRange;

          dispatch(
            downloadCompanyEmployeesPayslips({
              companyId,
              startDate: startDate.format(ISO_DATE_FORMAT),
              endDate: endDate.format(ISO_DATE_FORMAT),
              key,
              format: 'pdf',
            })
          );
          return;
        }

        setFormatModalVisibility(true);
      },
      [companyId, dateRange, dispatch]
    );

    const refreshSilaeEmployee = useCallback(() => {
      if (lastSyncingStatus !== 'in_process') {
        dispatch(refreshSilaeEmployees({ companyId, dateRange }));
      }
    }, [companyId, dateRange, dispatch, lastSyncingStatus]);

    const handleMenuClick = useCallback(
      (e) => {
        downloadCompanyEmployeesPayslipsHandler(e);
      },
      [downloadCompanyEmployeesPayslipsHandler]
    );

    const handleChangeMonth = useCallback(
      (month) => {
        const startOfMonth = moment(month).startOf('month');
        const endOfMonth = moment(month).endOf('month');
        onRangeChange([startOfMonth, endOfMonth]);
      },
      [onRangeChange]
    );

    const downloadMenu = (
      <Menu onClick={handleMenuClick}>
        {menuOptions.map((el) => (
          <Menu.Item key={el.value}>
            <DownloadOutlined className={Styles.downloadIcon} />
            {el.label}
          </Menu.Item>
        ))}
      </Menu>
    );

    return (
      <>
        <div className={Styles.wrapper}>
          <div className={Styles.headerRow}>
            <PageTitle>{SALARY_HEADER_LABELS.EMPLOYEES}</PageTitle>
            {managedSilae && (
              <div className={Styles.refreshWrapper}>
                {getSilaeRefreshEmployeesTranslation(lastSyncTime)}
                <Tooltip title={SILAE_SYNCING}>
                  <div
                    className={Styles.refreshIconWrapper}
                    onClick={refreshSilaeEmployee}
                  >
                    <img
                      className={cn(Styles.refreshIcon, {
                        [Styles.refreshIconLoading]:
                          lastSyncingStatus === 'in_process',
                      })}
                      src={refreshIcon}
                      alt="sync silae"
                    />
                  </div>
                </Tooltip>
              </div>
            )}

            <WaitingModal />
            <SingleWaitingModal />
            <ChoseFormatModal
              isVisible={isFormatModalVisible}
              companyId={companyId}
              dateRange={dateRange}
              setFormatModalVisibility={setFormatModalVisibility}
            />

            <div className={Styles.buttonsWrapperMobile}>
              {managedSilae && (
                <Dropdown.Button
                  overlay={downloadMenu}
                  onClick={downloadCompanyEmployeesPayslipsHandler}
                  className={Styles.downloadButton}
                >
                  <DownloadOutlined className={Styles.downloadIcon} />
                  {DOWNLOAD_PAYSLIP}
                </Dropdown.Button>
              )}
              {!managedSilae && (
                <Button
                  sizeAuto
                  variant="transparent"
                  className={Styles.downloadButton}
                  onClick={downloadTableXlsxHandler}
                >
                  <DownloadOutlined className={Styles.downloadIcon} />
                  {DOWNLOAD}
                </Button>
              )}
            </div>
            <NavLink
              to={ROUTES.EMPLOYEE_REGISTER_TITLE}
              className={Styles.newButtonMobile}
            >
              <Button sizeAuto variant="secondary" className={undefined}>
                {NEW_EMPLOYEE}
              </Button>
            </NavLink>
            <div className={Styles.buttonsWrapper}>
              {managedSilae && (
                <Dropdown.Button
                  overlay={downloadMenu}
                  onClick={downloadCompanyEmployeesPayslipsHandler}
                  className={Styles.downloadButton}
                >
                  <DownloadOutlined className={Styles.downloadIcon} />
                  {DOWNLOAD_PAYSLIP}
                </Dropdown.Button>
              )}

              <NavLink to={ROUTES.EMPLOYEE_REGISTER_TITLE}>
                <Button sizeAuto className={Styles.newButton}>
                  {NEW_EMPLOYEE}
                </Button>
              </NavLink>
            </div>
          </div>
          {isMobileVersion && isInputVisible && (
            <Input
              ref={searchInputRef}
              placeholder={FILTER_PLACEHOLDERS.SEARCH_BY_NAME}
              prefix={<SearchOutlined className={Styles.searchIconPrefix} />}
              className={Styles.searchInputMobile}
              onChange={onSearchChange}
              value={searchTerm}
              allowClear
            />
          )}
          <div className={Styles.filtersRow}>
            <MonthPicker
              value={
                (moment(dateRange.startDate)
                  .startOf('month')
                  .isSame(dateRange.startDate) &&
                  moment(dateRange.endDate)
                    .endOf('month')
                    .isSame(dateRange.endDate) &&
                  dateRange.startDate) ||
                undefined
              }
              setValue={handleChangeMonth}
              className={Styles.monthPicker}
              format={MONTH_PICKER_FORMAT}
              locale={moment.locale('fr', {
                monthsShort: SHORT_MONTH_FR,
              })}
              placeholder="Mois"
            />

            <RangePicker
              className={Styles.rangePicker}
              suffixIcon={
                <CalendarMiniIcon style={{ color: 'var(--limeGreen)' }} />
              }
              value={[dateRange.startDate, dateRange.endDate]}
              onChange={onRangeChange}
            />

            <div className={Styles.iconsWrapper}>
              <div className={Styles.filterIconWrapper} onClick={openModal}>
                {hasFilters && <div className={Styles.filterIconBadge}></div>}
                <img src={iconFilterList} alt="icon-filter-list" />
              </div>
              <div className={Styles.searchIconWrapper} onClick={toggleInput}>
                <SearchOutlined className={Styles.searchIcon} />
              </div>
            </div>
            <div className={Styles.row}>
              <CustomSelect
                size="large"
                name="establishments"
                className={Styles.select}
                options={branchesOptions}
                onChange={onSelectChange}
                value={filters.establishments}
                dropdownMatchSelectWidth={false}
                placeholder={FILTER_PLACEHOLDERS.ESTABLISHMENT}
                allowClear
              />
              <CustomSelect
                size="large"
                name="natureEmployments"
                className={Styles.select}
                options={sortedNatureEmploymentsOptions}
                onChange={onSelectChange}
                value={filters.natureEmployments}
                dropdownMatchSelectWidth={false}
                placeholder={FILTER_PLACEHOLDERS.NATURE_EMPLOYMENT}
                allowClear
              />
              <CustomSelect
                size="large"
                name="classifications"
                className={Styles.select}
                options={sortedClassificationsOptions}
                onChange={onSelectChange}
                value={filters.classifications}
                placeholder={FILTER_PLACEHOLDERS.CLASSIFICATION}
                allowClear
              />
              <CustomSelect
                size="large"
                name="contracts"
                className={Styles.select}
                options={sortedContractsOptions}
                onChange={onSelectChange}
                value={filters.contracts}
                placeholder={FILTER_PLACEHOLDERS.CONTRACT}
                allowClear
              />
              <Input
                size="large"
                allowClear
                placeholder={FILTER_PLACEHOLDERS.SEARCH_BY_NAME}
                prefix={<SearchOutlined className={Styles.searchIconPrefix} />}
                onChange={onSearchChange}
                value={searchTerm}
                className={Styles.searchInput}
              />
            </div>
          </div>
        </div>
        {isMobileVersion && isModalVisible && (
          <MobileFilters
            isVisible={isModalVisible}
            onClose={closeModal}
            onSubmit={handleSubmit}
            initialValues={filters}
          />
        )}
      </>
    );
  }
);

FiltersToolbar.propTypes = propTypes;

const WaitingModal = () => {
  const isWaitingModalShow = useSelector(selectIsWaitingModalShow);

  return (
    <Modal
      centered
      destroyOnHidden
      open={Boolean(isWaitingModalShow)}
      footer={false}
      className={Styles.modal}
      closable={false}
      title={
        <div className={Styles.modalTitleWrapper}>
          {WAITING_MODAL.DOWNLOAD} <Spin />
        </div>
      }
    >
      {typeof isWaitingModalShow === 'string'
        ? WAITING_MODAL.OTHER_DOCUMENTS_TEXT
        : WAITING_MODAL.TEXT}
    </Modal>
  );
};

const SingleWaitingModal = () => {
  const isSingleWaitingModalShow = useSelector(selectIsSingleWaitingModalShow);

  return (
    <Modal
      centered
      destroyOnHidden
      open={isSingleWaitingModalShow}
      footer={false}
      className={Styles.modal}
      closable={false}
      title={
        <div className={Styles.modalTitleWrapper}>
          {SINGLE_WAITING_MODAL.DOWNLOAD} <Spin />
        </div>
      }
    >
      {SINGLE_WAITING_MODAL.TEXT}
    </Modal>
  );
};

const FORMAT_CARDS = [
  {
    id: 'pdf',
    icon: <FilePdfOutlined size="large" />,
    title: CHOSE_MODAL.PDF.TITLE,
    description: CHOSE_MODAL.PDF.DESCRIPTION,
  },

  {
    id: 'zip',
    icon: <FileZipOutlined size="large" />,
    title: CHOSE_MODAL.ZIP.TITLE,
    description: CHOSE_MODAL.ZIP.DESCRIPTION,
  },
];

const FormatCard = ({ isSelected, icon, title, description, onClick }) => (
  <div
    className={cn(Styles.card, { [Styles.selectedCard]: isSelected })}
    onClick={onClick}
  >
    <div className={Styles.cardIcon}>{icon}</div>
    <div className={Styles.cardTitle}>{title}</div>
    <div className={Styles.cardDescription}>{description}</div>
  </div>
);

const ChoseFormatModal = memo(
  ({ isVisible, companyId, dateRange, setFormatModalVisibility }) => {
    const dispatch = useDispatch();
    const [selectedFormat, setSelectedFormat] = useState(null);

    const onCardClick = (id) => () => {
      setSelectedFormat(id);
    };

    const downloadCompanyEmployeesPayslipsHandler = useCallback(() => {
      const { startDate, endDate } = dateRange;
      dispatch(
        downloadCompanyEmployeesPayslips({
          companyId,
          startDate: startDate.format(ISO_DATE_FORMAT),
          endDate: endDate.format(ISO_DATE_FORMAT),
          format: selectedFormat,
        })
      );
      setFormatModalVisibility(false);
    }, [
      dateRange,
      dispatch,
      companyId,
      selectedFormat,
      setFormatModalVisibility,
    ]);

    return (
      <Modal
        centered
        destroyOnHidden
        open={isVisible}
        footer={
          <div className={Styles.choseModalFooter}>
            <Button
              variant="primary"
              disabled={!selectedFormat}
              onClick={downloadCompanyEmployeesPayslipsHandler}
            >
              {BUTTONS_LABELS.DOWNLOAD}
            </Button>
          </div>
        }
        className={Styles.choseModal}
        closable
        title={
          <div className={Styles.modalTitleWrapper}>{CHOSE_MODAL.TITLE}</div>
        }
        onCancel={() => setFormatModalVisibility(false)}
      >
        <div className={Styles.choseFormatWrapper}>
          {FORMAT_CARDS.map((card) => {
            const isSelected = selectedFormat === card.id;

            return (
              <FormatCard
                key={card.id}
                isSelected={isSelected}
                icon={card.icon}
                title={card.title}
                description={card.description}
                onClick={onCardClick(card.id)}
              />
            );
          })}
        </div>
      </Modal>
    );
  }
);
